import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useAuthStore, readPersistedAuth } from '../store/auth.store';
import type { AuthUser } from '../types';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/cms-portal';

export interface AdminTokens {
  accessToken: string;
  /** Single use — every refresh returns a new one. */
  refreshToken: string;
  expiresIn: number;
  refreshExpiresAt: string;
}

export type AdminLoginResponse =
  | (AdminTokens & {
      requiresTotp: false;
      mustChangePassword: boolean;
      mustEnrollTotp: boolean;
    })
  | {
      requiresTotp: true;
      /** Lives 5 minutes. Keep in memory only. */
      challengeToken: string;
    };

export type AdminChangePasswordResponse = AdminTokens & { success: true };

export interface TotpSetupResponse {
  secret: string;
  otpauthUrl: string;
}

export interface TotpEnableResponse {
  recoveryCodes: string[];
}

export const TOTP_CHALLENGE_TTL_MS = 5 * 60 * 1000;

const unwrap = <T>(data: any): T => (data?.data ?? data) as T;

/** Builds the session user from the admin access token claims. */
export function adminUserFromToken(accessToken: string, loginIdentifier: string): AuthUser {
  let decoded: any = {};
  try {
    decoded = jwtDecode(accessToken);
  } catch {
    // ignore
  }
  return {
    id: decoded.sub || decoded.id || 'admin-id',
    full_name: decoded.username || decoded.name || loginIdentifier,
    email: decoded.email || (loginIdentifier.includes('@') ? loginIdentifier : `${loginIdentifier}@admin.cms`),
    role: 'platform_admin',
    avatar: '',
  };
}

// ─── Single-flight refresh ────────────────────────────────────────────────────
// The refresh token is single use: two concurrent refreshes with the same token are
// treated as theft and revoke every session. So within a tab only one refresh promise
// may be in flight, and across tabs the refresh runs under a Web Lock. Inside the lock
// we re-read the persisted tokens — if another tab already rotated them we reuse those.

const REFRESH_LOCK_NAME = 'homehub-admin-token-refresh';
let inflightRefresh: Promise<AdminTokens | { accessToken: string; refreshToken: string }> | null = null;

async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks?.request) {
    return locks.request(REFRESH_LOCK_NAME, { mode: 'exclusive' }, fn) as Promise<T>;
  }
  return fn();
}

/**
 * Refreshes the admin token pair once, no matter how many callers ask concurrently.
 * @param failedAccessToken the access token the failed request was sent with
 */
export function refreshAdminTokensOnce(failedAccessToken?: string | null) {
  inflightRefresh ??= withRefreshLock(async () => {
    const store = useAuthStore.getState();
    const persisted = readPersistedAuth();

    // Another tab already rotated the pair while we waited for the lock.
    if (failedAccessToken && persisted?.accessToken && persisted.refreshToken && persisted.accessToken !== failedAccessToken) {
      if (persisted.accessToken !== store.accessToken) {
        store.setTokens(persisted.accessToken, persisted.refreshToken);
      }
      return { accessToken: persisted.accessToken, refreshToken: persisted.refreshToken };
    }

    const refreshToken = persisted?.refreshToken || store.refreshToken;
    if (!refreshToken) throw new Error('No refresh token');

    const { data } = await axios.post(`${API_BASE_URL}/v1/admin/auth/refresh`, { refreshToken });
    const tokens = unwrap<AdminTokens>(data);
    useAuthStore.getState().setTokens(tokens.accessToken, tokens.refreshToken);
    return tokens;
  }).finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

// ─── Public auth endpoints (no interceptor) ──────────────────────────────────

export async function adminLogin(login: string, password: string) {
  const { data } = await axios.post(`${API_BASE_URL}/v1/admin/auth/login`, { login, password });
  return unwrap<AdminLoginResponse>(data);
}

export async function adminLoginTotp(challengeToken: string, code: string) {
  const { data } = await axios.post(`${API_BASE_URL}/v1/admin/auth/login/totp`, { challengeToken, code });
  return unwrap<AdminLoginResponse>(data);
}
